import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import api from '../api/client';

export function useAmazonSalesUpload(loadSalesData, loadMonthlySalesData) {
  const [showPopup, setShowPopup]         = useState(false);
  const [periodMode, setPeriodMode]       = useState('weekly');
  const [uploadWeek, setUploadWeek]       = useState('');
  const [uploadMonth, setUploadMonth]     = useState('');
  const [uploadYear, setUploadYear]       = useState('');
  const [selectedFile, setSelectedFile]   = useState(null);
  const [parsedData, setParsedData]       = useState([]);
  const [uploadStatus, setUploadStatus]   = useState(null);
  const fileInputRef                      = useRef(null);

  const reset = () => {
    setUploadWeek('');
    setUploadMonth('');
    setUploadYear('');
    setSelectedFile(null);
    setParsedData([]);
    setUploadStatus(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openPopup  = () => setShowPopup(true);
  const closePopup = () => { setShowPopup(false); reset(); };

  const handlePeriodModeChange = (mode) => {
    setPeriodMode(mode);
    setParsedData([]);
    setUploadStatus(null);
    setUploadWeek('');
    setUploadMonth('');
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setParsedData([]);
    setUploadStatus(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const data     = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const ws       = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(ws);
      const firstRow = jsonData[0] || {};

      const normalize = (k) => k.trim().toLowerCase();
      const keys      = Object.keys(firstRow).map(normalize);
      const hasASIN   = keys.some(k => k === 'asin' || k === '(parent) asin' || k === 'asin (child)');
      const hasUnits  = keys.some(k => ['units', 'quantity', 'qty'].includes(k));

      if (!hasASIN || !hasUnits) {
        setUploadStatus({ type: 'error', message: 'File must contain ASIN and Units columns' });
        return;
      }

      const formatted = jsonData.map((row, index) => {
        const r = Object.fromEntries(Object.entries(row).map(([k, v]) => [k.trim().toLowerCase(), v]));
        return {
          id:    index + 1,
          asin:  String(r.asin || r['(parent) asin'] || r['asin (child)'] || '').trim(),
          units: parseInt(r.units || r.quantity || r.qty || 0) || 0,
        };
      }).filter(r => r.asin);

      setParsedData(formatted);
    };
    reader.onerror = () => setUploadStatus({ type: 'error', message: 'Error reading file' });
    reader.readAsArrayBuffer(file);
  };

  const handleUploadSubmit = async (marketplaceConfig) => {
    const isMonthly = periodMode === 'monthly';
    const endpoint  = isMonthly
      ? (marketplaceConfig.endpoints.uploadMonthSales || '/api/upload-amazon-month-sales')
      : marketplaceConfig.endpoints.uploadSales;

    if (!endpoint) {
      setUploadStatus({ type: 'error', message: 'Upload not supported for this marketplace' });
      return;
    }
    if (!selectedFile || !uploadYear || (isMonthly ? !uploadMonth : !uploadWeek)) {
      setUploadStatus({ type: 'error', message: 'Please fill all fields and select a file' });
      return;
    }
    if (parsedData.length === 0) {
      setUploadStatus({ type: 'error', message: 'No valid data found in the file' });
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('year', uploadYear);
    if (isMonthly) {
      formData.append('month', uploadMonth);
    } else {
      formData.append('weeknumber', uploadWeek);
    }

    setUploadStatus({ type: 'loading', message: 'Uploading data...' });
    try {
      const { data: result } = await api.post(endpoint, formData);
      setUploadStatus({ type: 'success', message: result.message || 'Data uploaded successfully' });
      if (isMonthly) {
        loadMonthlySalesData?.();
      } else {
        loadSalesData?.();
      }
      setTimeout(() => { closePopup(); }, 3000);
    } catch (err) {
      if (err.response) {
        setUploadStatus({ type: 'error', message: err.response.data?.error || 'Upload failed' });
      } else {
        setUploadStatus({ type: 'error', message: `Error: ${err.message}` });
      }
    }
  };

  return {
    showPopup, setShowPopup,
    periodMode, setPeriodMode,
    uploadWeek, setUploadWeek,
    uploadMonth, setUploadMonth,
    uploadYear, setUploadYear,
    selectedFile, setSelectedFile,
    parsedData, setParsedData,
    uploadStatus, setUploadStatus,
    fileInputRef,
    openPopup,
    closePopup,
    handlePeriodModeChange,
    handleFileUpload,
    handleUploadSubmit,
  };
}